import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { useAuthStore } from "@/stores/Auth";
import { fetchSectorRelatedSecurities, peekSectorRelatedSecurities, resetConstituentsCache } from "@/api/sector-constituents";
import { clientStateManager } from "@/services/error-services";
import { formatLocalePercentage, formatPriceAsCurrency } from "@tmi/utils/formatting";

import { goFetchOrThrow } from "@/utils/network-utils";
import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import { NotFound } from "@tmi/types/api/responses";

import { RelatedData, SectorInfo, SectorWithRelatedData, SecurityInfo, SecurityWithRelatedDataBySector, ValueData } from "@tmi/types/related";
import type { SectorRelatedSecuritiesApiResponse, RelatedSecuritiesDataApiResponse, RelatedSecurityApiResponse, StockWithRelatedSecuritiesBySectorApiResponse } from "@tmi/types/api/related";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

function createSimpleTableRows( data: RelatedSecurityApiResponse[] ): ValueData[] {
    return data.map( ( data: RelatedSecurityApiResponse ): ValueData => ( {
        title: data.name,
        symbol: data.symbol,
        six_mo_perf: data.six_mo_perf,
        seven_minus_one_mo_perf: data.seven_minus_one_mo_perf,
        last_close: data.close
    } ) );
}

function buildRelatedDataTable( data: RelatedSecurityApiResponse[], isStock: boolean ): RelatedData {
    return {
        headers: [
            { text: 'Ticker', align: 'start', value: 'symbol' },
            { text: isStock ? 'Company' : 'Fund', value: 'title' },
            { text: 'Last Close Price', value: 'last_close' },
            { text: '6 mo %', value: 'six_mo_perf' },
            { text: '7-1 mo %', value: 'seven_minus_one_mo_perf' }
        ],
        rows: createSimpleTableRows( data )
    };
}

function buildSecurityInfo( aggregateData: StockWithRelatedSecuritiesBySectorApiResponse ): SecurityInfo {
    return {
        symbol: aggregateData.symbol,
        name: aggregateData.name,
        six_mo_perf: `${ formatLocalePercentage( aggregateData.six_mo_perf ) }`,
        seven_minus_one_mo_perf: `${ formatLocalePercentage( aggregateData.seven_minus_one_mo_perf ) }`,
        last_close: `${ formatPriceAsCurrency( aggregateData.last_close || 0.0 ) }`,
        market_rs: ( aggregateData.market_rs || '-' )
    };
}

function buildSectorData( sector: SectorRelatedSecuritiesApiResponse ): SectorInfo {
    return {
        id: sector.id,
        symbol: sector.symbol,
        name: sector.name,
        bpiStatus: sector.bpi_status,
        bpiLastStatusChangeDate: sector.bpi_rev_dt,
        bpiSignal: sector.bpi_signal,
        bpiColumn: sector.bpi_column,

        rs: sector.rs,
        rsRank: sector.rs_rank,
        rsColumn: sector.rs_column,
        rsReversalDate: sector.rs_rev_dt
    };
}

function buildRelatedData( dataBySector: SectorRelatedSecuritiesApiResponse | null | undefined ): SectorWithRelatedData | null {
    if ( null == dataBySector ) {
        return null;
    }

    const sector: SectorInfo = buildSectorData( dataBySector );
    const relatedStocks: RelatedData = buildRelatedDataTable( dataBySector.stocks, true );
    const relatedEtfs: RelatedData = buildRelatedDataTable( dataBySector.etfs, false );

    return {
        sector: sector,
        stocks: relatedStocks,
        etfs: relatedEtfs
    };
}

// DP-14 (tmi-e7w.14): a security in several sectors has no primary one. The Security page lists its sectors and
// selects the first by default, so they are ordered by sector id here (Joel's ruling: lowest id first) instead of
// following the server's row order.
function buildRelatedDataBySector( sectors?: SectorRelatedSecuritiesApiResponse[] ): SectorWithRelatedData[] {
    const relatedData: SectorWithRelatedData[] = [] as unknown as SectorWithRelatedData[];
    [ ...( sectors ?? [] ) ].sort( ( a, b ) => ( a?.id ?? 0 ) - ( b?.id ?? 0 ) ).forEach( ( sectorResponse: SectorRelatedSecuritiesApiResponse | null | undefined ) => {
        const result = buildRelatedData( sectorResponse );
        if ( result ) {
            relatedData.push( result );
        }
    } );

    return relatedData;
}

function buildSecurityWithRelatedDataBySector( aggregateData: StockWithRelatedSecuritiesBySectorApiResponse ): SecurityWithRelatedDataBySector {
    return {
        security: buildSecurityInfo( aggregateData ),
        related_data: buildRelatedDataBySector( aggregateData?.related_securities_by_sector?.sectors )
    }
}

// Rank 13: the Security page's own request state. It had one `loading` flag, no error or miss state, and no request
// identity, so a failed load left the page blank and a slow response for the previous ticker landed under the new URL.
const securityLoading = ref< boolean >( false );
const securityFailed = ref< boolean >( false );
const securityMissing = ref< boolean >( false );
const isLoadingSecurity = computed< boolean >( () => securityLoading.value );
const hasSecurityError = computed< boolean >( () => securityFailed.value );
const isSecurityNotFound = computed< boolean >( () => securityMissing.value );
// Only the latest security request may write the page's data, and only for the ticker it is showing.
let securityRequest = 0;
let securityRequestTicker = '';
const relatedDataBySector = ref< SecurityWithRelatedDataBySector | null >( null );
const sectorRelatedData = ref< SectorWithRelatedData | null >( null );

// Rank 10: the sector flyout's constituent tables have their own flags. They shared `loading` with the Security page,
// so the flyout hid its locally computed stats and P&F chart behind this request, and opening a sector over a
// security page blanked that page too.
const sectorTablesLoading = ref< boolean >( false );
const sectorTablesFailed = ref< boolean >( false );
const isLoadingSectorTables = computed< boolean >( () => sectorTablesLoading.value );
const hasSectorTablesError = computed< boolean >( () => sectorTablesFailed.value );
// Only the latest sector request may write the tables: switching sectors quickly let an earlier, slower response
// land last and show the previous sector's stocks.
let sectorTablesRequest = 0;

export const useSecuritiesStore = defineStore( 'security', () => {
    const { authToken } = storeToRefs( useAuthStore() );

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        securityRequest += 1;
        securityRequestTicker = '';
        securityLoading.value = false;
        securityFailed.value = false;
        securityMissing.value = false;
        relatedDataBySector.value = null;
        sectorRelatedData.value = null;

        sectorTablesRequest += 1;
        sectorTablesLoading.value = false;
        sectorTablesFailed.value = false;
        resetConstituentsCache();
    }

    /**
     * Loads the Security page's data for a ticker. Resolves once it is settled; a failure is reported here and left in
     * `hasSecurityError`, never thrown. Another ticker's data is cleared at once so it is never shown under this URL,
     * while a refresh of the same ticker (a new market date) keeps what is on screen until the answer lands.
     */
    async function loadSecurityDataForDate( ticker: string, date: string ): Promise< void > {
        // tmi-ck2 C4 signed-out guard. `sentient/related-securities-by-sector` carries
        // .RequireAuthorization, so a token-less call is a guaranteed 401; skip it rather than
        // report a failure for a page the signed-out visitor cannot see anyway.
        if ( !authToken.value ) {
            return;
        }

        const request = ++securityRequest;
        const tickerKey = ticker.trim().toUpperCase();
        if ( tickerKey !== securityRequestTicker ) {
            relatedDataBySector.value = null;
        }

        securityRequestTicker = tickerKey;
        securityLoading.value = true;
        securityFailed.value = false;
        securityMissing.value = false;

        const isCurrent = (): boolean => request === securityRequest && tickerKey === securityRequestTicker;
        try {
            // goFetchOrThrow: plain goFetch resolves null on a timeout or a dropped connection, which read as "no data"
            // here and replaced the card already on screen with a miss that offered no retry.
            const response: ApiFetchResponse< RelatedSecuritiesDataApiResponse > | null | undefined =
                // tmi-ck2 C4: `relatedSecuritiesBySector` (was persisted query
                // b48a508a97258c4ffd8f4b8884bd53c3). The query-string names are the GraphQL ARGUMENT
                // names verbatim — `ticker` and `date` — which is what the twin binds.
                await goFetchOrThrow< RelatedSecuritiesDataApiResponse, never >( {
                    serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                    endpoint: `sentient/related-securities-by-sector?ticker=${ encodeURIComponent( ticker ) }&date=${ encodeURIComponent( date ) }`,
                    verbOverride: 'GET',
                    authToken: authToken.value
                } );
            if ( !isCurrent() ) {
                return;
            }

            const stockWithRelatedSecuritiesBySector = response?.data?.relatedSecuritiesBySector;
            if ( stockWithRelatedSecuritiesBySector ) {
                relatedDataBySector.value = buildSecurityWithRelatedDataBySector( stockWithRelatedSecuritiesBySector );
            } else {
                // A miss used to leave the previous answer on screen.
                relatedDataBySector.value = null;
                securityMissing.value = true;
                logger.warn( `Data was not found for ticker '${ ticker }' for date: ${ date }` );
            }
        } catch ( err: unknown ) {
            if ( !isCurrent() ) {
                return;
            }

            // A 404 is the server having no such ticker: the same miss as a response without the payload.
            if ( err instanceof NotFound ) {
                relatedDataBySector.value = null;
                securityMissing.value = true;
                logger.warn( `Data was not found for ticker '${ ticker }' for date: ${ date }` );
                return;
            }

            // A same-ticker refresh that fails keeps the data already shown; the page shows the error only without it.
            securityFailed.value = true;
            logger.error( `Security data for '${ ticker }' for ${ date } failed to load`, err );
            clientStateManager.queueClientError( err, {
                token: authToken.value,
                request: 'security-related-data'
            } );
        } finally {
            if ( isCurrent() ) {
                securityLoading.value = false;
            }
        }
    }

    /**
     * Loads the constituent tables of a sector. Resolves when they are on screen, but the flyout does not wait on it:
     * its stats and chart are local data. A (sector, date) fetched in the last half hour, by the flyout or the
     * dashboard's Sector Stock Leaders, is shown at once without a request.
     */
    async function loadSectorDataForDate( symbol: string, dateAsString: string ): Promise< void > {
        // tmi-ck2 C4 signed-out guard — see loadSecurityDataForDate. The twin this one reads,
        // `sentient/related-securities-for-sector`, requires authorization as well.
        if ( !authToken.value ) {
            return;
        }

        const request = ++sectorTablesRequest;
        sectorTablesFailed.value = false;

        const cached = peekSectorRelatedSecurities( symbol, dateAsString );
        if ( cached ) {
            sectorRelatedData.value = buildRelatedData( cached );
            sectorTablesLoading.value = false;
            return;
        }

        sectorRelatedData.value = null;
        sectorTablesLoading.value = true;
        try {
            const sector = await fetchSectorRelatedSecurities( symbol, dateAsString, authToken.value );
            if ( request !== sectorTablesRequest ) {
                return;
            }

            // A miss empties only the tables: it used to call reset(), which also wiped the Security page's data.
            sectorRelatedData.value = buildRelatedData( sector );
        } catch ( err: unknown ) {
            if ( request !== sectorTablesRequest ) {
                return;
            }

            sectorRelatedData.value = null;
            sectorTablesFailed.value = true;
            logger.error( `Constituents of sector '${ symbol }' for ${ dateAsString } failed to load`, err );
            clientStateManager.queueClientError( err, {
                token: authToken.value,
                request: 'sector-constituents'
            } );
        } finally {
            if ( request === sectorTablesRequest ) {
                sectorTablesLoading.value = false;
            }
        }
    }

    return {
        // ACTIONS
        loadSectorDataForDate,
        loadSecurityDataForDate,
        reset,

        // GETTERS
        hasSectorTablesError,
        hasSecurityError,
        isLoadingSecurity,
        isLoadingSectorTables,
        isSecurityNotFound,
        relatedDataBySector,
        sectorRelatedData
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSecuritiesStore, import.meta.hot ) );
}